/**
 * DemoNav — shared, dismissible cross-navigation bar.
 *
 * Every runnable CSK demo SPA mounts this so a visitor on any one demo can
 * reach the other six (and the launcher). It is deliberately dependency-free
 * — only React and inline styles — so SPAs that do not otherwise pull in the
 * full `@csk/demo-ui` surface (or Tailwind/tokens) can mount it cheaply via
 * the `@csk/demo-ui/catalog` subpath.
 *
 * The bar is dismissible; the choice is remembered per browser in
 * `localStorage` so it does not nag across reloads. A small re-open handle
 * stays in the corner once dismissed.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  WORKSPACE_DEMO_CATALOG,
  demoByScript,
  demoLocalUrl,
  type DemoCatalogWorkspaceEntry,
} from "./catalog-data.js";
import navVisuals from "./demo-nav-visuals.data.json" with { type: "json" };

export interface DemoNavProps {
  /**
   * The `demo:*` script of the SPA mounting this bar. Used to label "you are
   * here" and to drop the current demo from the cross-links. Omit on the
   * launcher.
   */
  readonly currentScript?: string;
  /** Dock the bar at the top instead of the bottom. Default: bottom. */
  readonly position?: "top" | "bottom";
}

const STORAGE_KEY = "csk-demo-nav-dismissed";

function readDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistDismissed(dismissed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (dismissed) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode / disabled) — bar still works in-session */
  }
}

const COLORS = navVisuals.colors;

export function DemoNav({ currentScript, position = "bottom" }: DemoNavProps): React.JSX.Element {
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [showDevDetails, setShowDevDetails] = useState<boolean>(false);

  // Read persisted state after mount so server/first paint is deterministic.
  useEffect(() => {
    setDismissed(readDismissed());
    setShowDevDetails(new URLSearchParams(window.location.search).get("dev") === "1");
  }, []);

  const setAndPersist = (next: boolean): void => {
    setDismissed(next);
    persistDismissed(next);
  };

  const dockStyle: React.CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    zIndex: 2147483000,
    ...(position === "top" ? { top: 0 } : { bottom: 0 }),
  };

  // The per-port nav bar is itself a per-port concept — it links to
  // sibling demos by their per-port URL. Hub-internal demos have no
  // per-port server, so they are not listed here; reach those through
  // the hub launcher (`npm run demo:hub`).
  const { others, current } = useMemo(() => {
    const nextOthers: DemoCatalogWorkspaceEntry[] = [];
    for (const entry of WORKSPACE_DEMO_CATALOG) {
      if (entry.script !== currentScript) nextOthers.push(entry);
    }
    return {
      others: nextOthers,
      current: currentScript ? demoByScript(currentScript) : undefined,
    };
  }, [currentScript]);

  if (dismissed) {
    return (
      <div style={{ ...dockStyle, pointerEvents: "none" }}>
        <button
          type="button"
          onClick={() => {
            setAndPersist(false);
          }}
          style={{
            pointerEvents: "auto",
            position: "absolute",
            right: "0.75rem",
            ...(position === "top" ? { top: "0.5rem" } : { bottom: "0.5rem" }),
            background: COLORS.bar,
            color: COLORS.dim,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "9999px",
            fontSize: "0.7rem",
            padding: "0.3rem 0.7rem",
            cursor: "pointer",
          }}
          aria-label="Show other CSK demos"
        >
          ⋯ Other CSK demos
        </button>
      </div>
    );
  }

  return (
    <nav
      aria-label="Other CSK demos"
      style={{
        ...dockStyle,
        background: COLORS.bar,
        color: COLORS.text,
        borderTop: position === "bottom" ? `1px solid ${COLORS.border}` : undefined,
        borderBottom: position === "top" ? `1px solid ${COLORS.border}` : undefined,
        padding: "0.5rem 0.85rem",
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        flexWrap: "wrap",
        fontFamily: "var(--font-hyperlegible), var(--csk-font-hyperlegible), system-ui, sans-serif",
        fontSize: "0.78rem",
        boxShadow:
          position === "bottom"
            ? "0 -10px 28px rgba(42,50,70,0.12)"
            : "0 10px 28px rgba(42,50,70,0.12)",
      }}
    >
      <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>CSK demos</span>
      {current ? (
        <span style={{ color: COLORS.dim, whiteSpace: "nowrap" }}>
          you are on <strong style={{ color: COLORS.text }}>{current.title}</strong> ·
        </span>
      ) : null}

      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", flex: 1 }}>
        {others.map((entry) => (
          <a
            key={entry.script}
            href={demoLocalUrl(entry)}
            title={showDevDetails ? `${entry.summary} (${demoLocalUrl(entry)})` : entry.summary}
            style={{
              color: COLORS.text,
              background: COLORS.link,
              border: `1px solid ${COLORS.border}`,
              borderRadius: "0.375rem",
              padding: "0.25rem 0.6rem",
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            {entry.title}
            {showDevDetails ? (
              <span style={{ color: COLORS.dim, marginLeft: "0.4rem", fontSize: "0.7rem" }}>
                :{entry.port}
              </span>
            ) : null}
          </a>
        ))}
      </div>

      <button
        type="button"
        onClick={() => {
          setAndPersist(true);
        }}
        style={{
          background: "transparent",
          color: COLORS.dim,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "0.375rem",
          padding: "0.25rem 0.55rem",
          cursor: "pointer",
          fontSize: "0.72rem",
          whiteSpace: "nowrap",
        }}
        aria-label="Dismiss the cross-demo navigation bar"
      >
        Dismiss
      </button>
    </nav>
  );
}
